/**
 * TRACK 3: Global Error Monitoring Component
 * Captures window.onerror and unhandledrejection events for debugging
 */

'use client';

import { useEffect } from 'react';

interface ErrorSnapshot {
  timestamp: string;
  type: 'javascript-error' | 'unhandled-promise';
  error: {
    name?: string;
    message: string;
    stack?: string;
    filename?: string;
    lineno?: number;
    colno?: number;
  };
  url: string;
  userAgent: string;
  localStorage?: {
    [key: string]: boolean;
  };
  sessionData?: {
    currentPath: string;
    referrer: string;
    timeOnPage: number;
  };
}

export function ErrorMonitoring() {
  useEffect(() => {
    // Only run on client side
    if (typeof window === 'undefined') return;

    console.log('ERROR MONITORING: Initializing global error handlers...');

    // Store the start time for session tracking
    const pageStartTime = Date.now();

    const createErrorSnapshot = (
      type: ErrorSnapshot['type'], 
      error: any, 
      additional: any = {}
    ): ErrorSnapshot => {
      const snapshot: ErrorSnapshot = {
        timestamp: new Date().toISOString(),
        type,
        error: {
          name: error?.name || 'Unknown',
          message: error?.message || 'Unknown error',
          stack: error?.stack,
          ...additional,
        },
        url: window.location.href,
        userAgent: navigator.userAgent,
        sessionData: {
          currentPath: window.location.pathname,
          referrer: document.referrer,
          timeOnPage: Math.round((Date.now() - pageStartTime) / 1000),
        },
      };

      // Safely capture localStorage state
      try {
        snapshot.localStorage = {
          hasApplyDraft: !!localStorage.getItem('vgfs_apply_draft'),
          hasUtmParams: !!localStorage.getItem('vgfs_utm_params'),
          hasErrorSnapshot: !!localStorage.getItem('vgfs_error_snapshot'),
        };
      } catch (e) {
        console.error('ERROR MONITORING: Failed to capture localStorage state:', e);
      }

      return snapshot;
    };

    const storeErrorSnapshot = (snapshot: ErrorSnapshot) => {
      try {
        // Store in localStorage for debugging
        localStorage.setItem('vgfs_error_snapshot_latest', JSON.stringify(snapshot));
        
        // Keep a history of recent errors (max 5)
        const historyKey = 'vgfs_error_history';
        let history: ErrorSnapshot[] = [];
        
        try {
          const existing = localStorage.getItem(historyKey);
          if (existing) {
            history = JSON.parse(existing);
          }
        } catch (e) {
          console.error('ERROR MONITORING: Failed to parse error history:', e);
        }
        
        // Add new error to the beginning and limit to 5
        history.unshift(snapshot);
        history = history.slice(0, 5);
        
        localStorage.setItem(historyKey, JSON.stringify(history));
        
        console.error('ERROR MONITORING: Error snapshot stored:', snapshot);
      } catch (e) {
        console.error('ERROR MONITORING: Failed to store error snapshot:', e);
      }
    };

    // Global JavaScript error handler
    const handleGlobalError = (
      message: string | Event,
      filename?: string,
      lineno?: number,
      colno?: number,
      error?: Error
    ) => {
      console.error('ERROR MONITORING: Global JavaScript error detected:', {
        message, filename, lineno, colno, error
      });

      const snapshot = createErrorSnapshot('javascript-error', error || { message }, {
        filename,
        lineno,
        colno,
      });

      storeErrorSnapshot(snapshot);

      // Return false to allow default browser error handling
      return false;
    };

    // Unhandled promise rejection handler
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('ERROR MONITORING: Unhandled promise rejection:', event.reason);

      const snapshot = createErrorSnapshot('unhandled-promise', {
        message: event.reason?.message || String(event.reason),
        stack: event.reason?.stack,
        name: event.reason?.name || 'PromiseRejection',
      });

      storeErrorSnapshot(snapshot);

      // Don't prevent default handling
      return true;
    };

    // Install global error handlers
    window.onerror = handleGlobalError;
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    console.log('ERROR MONITORING: Global error handlers installed successfully');

    // Cleanup function
    return () => {
      console.log('ERROR MONITORING: Cleaning up global error handlers...');
      window.onerror = null;
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  // This component doesn't render anything
  return null;
}

/**
 * Helper function to retrieve and display error history (for debugging)
 */
export const getErrorHistory = (): ErrorSnapshot[] => {
  if (typeof window === 'undefined') return [];
  
  try {
    const history = localStorage.getItem('vgfs_error_history');
    return history ? JSON.parse(history) : [];
  } catch (e) {
    console.error('Failed to retrieve error history:', e);
    return [];
  }
};

/**
 * Helper function to clear error history
 */
export const clearErrorHistory = (): void => {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.removeItem('vgfs_error_history');
    localStorage.removeItem('vgfs_error_snapshot_latest');
    localStorage.removeItem('vgfs_error_snapshot');
    console.log('Error history cleared');
  } catch (e) {
    console.error('Failed to clear error history:', e);
  }
};